<template>
  <div class="flow-editor-overlay">
    <!-- Header -->
    <div class="flow-header">
      <div class="header-left">
        <i class="fas fa-project-diagram project-icon"></i>
        <h2 class="header-title">Workflow for {{ processTitle || 'Process' }}</h2>
      </div>
      <div class="header-right">
        <button class="btn btn-outline-secondary add-step-btn" @click="addNode">
          <i class="fas fa-plus me-2"></i> Add Steps
        </button>
        <button class="btn btn-primary save-workflow-btn" @click="saveWorkflow">
          Save Workflow
        </button>
        <button class="btn-close ms-3" @click="$emit('close')"></button>
      </div>
    </div>

    <!-- Main Editor -->
    <div class="flow-editor-container">
      <VueFlow
        v-model="elements"
        :default-viewport="{ x: 0, y: 0, zoom: 1 }"
        :min-zoom="0.05"
        :max-zoom="4"
        :fit-view-on-init="true"
        :edge-updater-radius="20"  :default-edge-options="{ type: 'smoothstep', animated: false, updatable: true }" 
        class="process-flow"
        @node-click="onNodeClick"
        @edge-click="onEdgeClick"
        @edge-update="onEdgeUpdate"
        @connect="onConnect"
      >
        <Background pattern-color="#aaa" :gap="16" />
        <Controls position="top-left" />
        
        <!-- Custom Node Slot -->
        <template #node-process="{ data, id, style }">
          <div 
            class="process-node" 
            :class="{ 'node-active': data.isActive, 'node-grey': data.isDraft }"
            :style="style"
          >
            <Handle type="target" position="left" id="in-left" class="custom-handle" />
            <Handle type="target" position="top" id="in-top" class="custom-handle" />
            
            <span class="node-label">{{ data.label || data.name }}</span>
            <div class="node-actions">
              <i class="fas fa-edit edit-icon" @click.stop="editNode(id, data)"></i>
              <i class="fas fa-trash-alt delete-icon" @click.stop="deleteNode(id)"></i>
            </div>

            <Handle type="source" position="bottom" id="out-bottom" class="custom-handle" />
            <Handle type="source" position="right" id="out-right" class="custom-handle" />
          </div>
        </template>

        <!-- Custom Plus Node -->
        <template #node-plus="{ id }">
          <div class="plus-node" @click="addNode">
            <i class="fas fa-plus"></i>
          </div>
        </template>
      </VueFlow>
    </div>

    <!-- Node Edit Panel -->
    <div v-if="editingNode" class="node-edit-panel">
      <!-- existing node edit content -->
      <div class="panel-header">
        <span>Edit Step</span>
        <i class="fas fa-times close-panel" @click="editingNode = null"></i>
      </div>
      <div class="panel-body">
        <div class="mb-3">
          <label class="form-label">Step Title</label>
          <input v-model="editingNode.data.label" type="text" class="form-control" />
        </div>
        <div class="form-check mb-3">
          <input v-model="editingNode.data.isDraft" class="form-check-input" type="checkbox" id="isDraft">
          <label class="form-check-label" for="isDraft">Draft Mode</label>
        </div>
        <button class="btn btn-sm btn-primary w-100" @click="confirmEdit">Apply Changes</button>
      </div>
    </div>

    <!-- Edge Edit Popup -->
    <div v-if="editingEdge" class="edge-edit-popup-overlay" @click.self="editingEdge = null">
      <div class="edge-edit-card">
        <div class="card-header-row">
          <span class="card-title">Edit transition</span>
          <i class="fas fa-times close-icon" @click="editingEdge = null"></i>
        </div>
        
        <div class="card-body">
          <input 
            v-model="editingEdge.label" 
            type="text" 
            class="form-control transition-input" 
            placeholder="Transition"
          />
          
          <div class="action-buttons-row">
            <button class="btn btn-save" @click="saveEdge">Save</button>
            <button class="btn btn-delete" @click="deleteEdge">Delete</button>
            <button class="btn btn-reverse" @click="reverseEdge">Reverse</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, defineProps, defineEmits, watch } from 'vue';
import { VueFlow, useVueFlow, Handle } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls } from '@vue-flow/controls';

// Import CSS
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';

const props = defineProps({
  processTitle: String,
  initialData: Object
});

const emit = defineEmits(['close', 'save']);

const { onPaneReady, toObject, setViewport, addNodes, addEdges, updateEdge } = useVueFlow();

const elements = ref([]);
const editingNode = ref(null);
const editingEdge = ref(null);

onMounted(() => {
  if (props.initialData && props.initialData.nodes) {
    // Normalize data: map default to process, marker_end to markerEnd
    const normalizedNodes = props.initialData.nodes.map(node => ({
      ...node,
      type: node.type === 'plus' ? 'plus' : 'process', // Map everything else to process for our UI
    }));

    const normalizedEdges = (props.initialData.edges || []).map(edge => ({
      ...edge,
      type: 'smoothstep',
      sourceHandle: edge.source_handle || edge.sourceHandle,
      targetHandle: edge.target_handle || edge.targetHandle,
      markerEnd: edge.marker_end || edge.markerEnd,
    }));

    // Check if plus node exists, if not add it at the end
    const plusNode = normalizedNodes.find(n => n.type === 'plus');
    if (!plusNode) {
      const lastNode = normalizedNodes.filter(n => n.type === 'process').pop();
      const posX = lastNode ? lastNode.position.x + 250 : 250;
      normalizedNodes.push({
        id: 'plus',
        type: 'plus',
        position: { x: posX + 150, y: 215 },
        draggable: false
      });
    }

    elements.value = [
      ...normalizedNodes,
      ...normalizedEdges
    ];
  } else {
    // Initial nodes if empty
    const initialNodes = [
      {
        id: '1',
        type: 'process',
        label: 'Start Process',
        position: { x: 250, y: 200 },
        data: { label: 'Research St...', isDraft: true }
      },
      {
        id: 'plus',
        type: 'plus',
        position: { x: 450, y: 215 },
        draggable: false
      }
    ];
    elements.value = initialNodes;
  }
});

const onConnect = (params) => {
  addEdges({
    id: `e_${params.source}_${params.target}_${Date.now()}`,
    ...params,
    label: 'Transition',
    type: 'smoothstep',
    animated: false,
    style: { stroke: '#6366f1', strokeWidth: 2 },
    labelStyle: { fill: '#64748b', fontWeight: 500, fontSize: '10px' },
    labelBgPadding: [4, 2],
    labelBgBorderRadius: 2,
    labelBgStyle: { fill: '#fff', fillOpacity: 0.7 },
    markerEnd: { type: 'arrowclosed', color: '#6366f1' },
  });
};

const addNode = () => {
  const lastNode = elements.value.filter(n => n.type === 'process').pop();
  const nextX = lastNode ? lastNode.position.x + 250 : 250;
  const newId = `node_${Date.now()}`;
  
  const newNode = {
    id: newId,
    type: 'process',
    position: { x: nextX, y: 200 },
    data: { label: `Draft Vara...`, isDraft: false }
  };

  // Move plus node
  const plusNode = elements.value.find(n => n.id === 'plus');
  if (plusNode) {
    plusNode.position.x = nextX + 150;
  }

  elements.value.push(newNode);

  // Auto connect if there was a last node
  if (lastNode) {
    onConnect({ 
      source: lastNode.id, 
      target: newId,
      type: 'smoothstep',
      sourceHandle: 'out-right',
      targetHandle: 'in-left'
    });
  }
};

const editNode = (id, data) => {
  editingNode.value = { id, data: { ...data } };
};

const onEdgeClick = ({ edge }) => {
  editingEdge.value = { ...edge };
};

const onEdgeUpdate = ({ edge, connection }) => {
  updateEdge(edge, connection);
  // elements.value = elements.value.map((el) => {
  //   if (el.id === edge.id) {
  //     return { 
  //       ...el, 
  //       source: connection.source, 
  //       target: connection.target,
  //       sourceHandle: connection.sourceHandle,
  //       targetHandle: connection.targetHandle
  //     };
  //   }
  //   return el;
  // });
};

const saveEdge = () => {
  const edge = elements.value.find(e => e.id === editingEdge.value.id);
  if (edge) {
    edge.label = editingEdge.value.label;
    if (edge.data) edge.data.name = editingEdge.value.label;
  }
  editingEdge.value = null;
};

const deleteEdge = () => {
  elements.value = elements.value.filter(el => el.id !== editingEdge.value.id);
  editingEdge.value = null;
};

const reverseEdge = () => {
  const edge = elements.value.find(e => e.id === editingEdge.value.id);
  if (edge) {
    const temp = edge.source;
    edge.source = edge.target;
    edge.target = temp;
    
    // Also swap handles if they exist
    const tempHandle = edge.sourceHandle;
    edge.sourceHandle = edge.targetHandle;
    edge.targetHandle = tempHandle;
  }
  editingEdge.value = null;
};

const confirmEdit = () => {
  const node = elements.value.find(n => n.id === editingNode.value.id);
  if (node) {
    node.data = { ...editingNode.value.data };
  }
  editingNode.value = null;
};

const deleteNode = (id) => {
  elements.value = elements.value.filter(el => el.id !== id && el.source !== id && el.target !== id);
  // Recalculate plus node position
  const lastNode = elements.value.filter(n => n.type === 'process').pop();
  const plusNode = elements.value.find(n => n.id === 'plus');
  if (plusNode) {
    plusNode.position.x = lastNode ? lastNode.position.x + 200 : 250;
  }
};

const saveWorkflow = () => {
  const flowObject = toObject();
  emit('save', flowObject);
};
</script>

<style scoped>
.flow-editor-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: white;
  z-index: 9999;
  display: flex;
  flex-direction: column;
}

.flow-header {
  height: 70px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 30px;
  background: white;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.project-icon {
  font-size: 24px;
  color: #6366f1;
}

.header-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.add-step-btn {
  border: 1px solid #e2e8f0;
  font-weight: 500;
}

.save-workflow-btn {
  background-color: #7c3aed;
  border: none;
  padding: 8px 20px;
  font-weight: 500;
}

.flow-editor-container {
  flex: 1;
  position: relative;
  background-color: #f8fafc;
}

.process-flow {
  width: 100%;
  height: 100%;
}

/* Custom Node Styles */
.process-node {
  padding: 10px 15px;
  border-radius: 8px;
  background: #6366f1; /* Default background */
  color: white;
  min-width: 160px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 2px solid #6366f1;
  transition: all 0.2s;
  position: relative;
}

.custom-handle {
  width: 12px !important;
  height: 12px !important;
  background-color: #fff !important;
  border: 1px solid #64748b !important;
  z-index: 10;
}

.node-grey {
  background: #94a3b8;
}

.node-active {
  border-color: #fbbf24;
}

.node-label {
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px;
}

.node-actions {
  display: flex;
  gap: 8px;
  opacity: 0.7;
}

.edit-icon, .delete-icon {
  font-size: 12px;
  cursor: pointer;
}

.edit-icon:hover, .delete-icon:hover {
  opacity: 1;
}

.plus-node {
  width: 36px;
  height: 36px;
  background: #818cf8;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  cursor: pointer;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;
}

.plus-node:hover {
  transform: scale(1.1);
}

.node-edit-panel {
  position: absolute;
  right: 20px;
  top: 90px;
  width: 300px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  z-index: 10;
}

.panel-header {
  padding: 15px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  font-weight: 600;
}

.panel-body {
  padding: 15px;
}

.close-panel {
  cursor: pointer;
  color: #94a3b8;
}

/* Edge Edit Popup Styling */
.edge-edit-popup-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.05); /* very subtle */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
}

.edge-edit-card {
  background: white;
  width: 440px;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.1);
  padding: 24px;
}

.card-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.card-title {
  font-size: 1.25rem;
  font-weight: 500;
  color: #1e293b;
}

.close-icon {
  cursor: pointer;
  color: #64748b;
  font-size: 1.1rem;
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.transition-input {
  border: 1px solid #e2e8f0;
  padding: 12px 16px;
  font-size: 1.1rem;
  border-radius: 10px;
}

.action-buttons-row {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.btn-save {
  background-color: #7c69ef;
  color: white;
  padding: 10px 28px;
  border-radius: 10px;
  font-weight: 500;
  border: none;
}

.btn-delete {
  background-color: #ef313c;
  color: white;
  padding: 10px 28px;
  border-radius: 10px;
  font-weight: 500;
  border: none;
}

.btn-reverse {
  background-color: white;
  color: #1e293b;
  padding: 10px 28px;
  border-radius: 10px;
  font-weight: 500;
  border: 1px solid #1e293b;
}

.btn-save:hover, .btn-delete:hover, .btn-reverse:hover {
  opacity: 0.9;
}
</style>
